import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { paths } from "../routes/paths";

// Spec: manna-web-ui §3.5 — two-key navigation sequences. Press `g` then a
// destination letter within 1.5 s to jump there. Mirrors gmail/github.
// Only registers on the global window; ignores when the user is typing
// inside a text field so list-search and form fields keep working.

const SEQUENCE_WINDOW_MS = 1500;

const GOTO_MAP: Record<string, string> = {
  d: paths.deals,
  c: paths.contacts,
  o: paths.organizations,
  a: paths.activities,
  t: paths.triage,
  p: paths.partners,
  s: paths.security,
};

function isTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useNavigationShortcuts() {
  const navigate = useNavigate();
  const armedAtRef = useRef<number | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTextInput(e.target)) return;

      const key = e.key.toLowerCase();

      if (armedAtRef.current != null) {
        const elapsed = Date.now() - armedAtRef.current;
        armedAtRef.current = null;
        if (elapsed > SEQUENCE_WINDOW_MS) return;
        const dest = GOTO_MAP[key];
        if (dest) {
          e.preventDefault();
          navigate(dest);
        }
        return;
      }

      if (key === "g") {
        armedAtRef.current = Date.now();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);
}
